import { useCallback, useEffect, useRef, useState } from "react";

import { useDashboardContext } from "metabase/dashboard/context";
import resizeObserver from "metabase/lib/resize-observer";

import { getNextShouldCollapseList } from "./use-responsive-parameter-list.utils";

type Opts = {
  reservedWidth: number;
  bufferSpace?: number;
};

export function useResponsiveParameterList({
  reservedWidth,
  bufferSpace = 24,
}: Opts) {
  const { isEditing, isEditingParameter } = useDashboardContext();

  const [shouldCollapseList, setShouldCollapseList] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const parameterListRef = useRef<HTMLDivElement>(null);

  const getWidths = useCallback(() => {
    if (!containerRef.current || !parameterListRef.current) {
      return null;
    }

    const { width: containerWidth } =
      containerRef.current.getBoundingClientRect();
    const { width: parametersWidth } =
      parameterListRef.current.getBoundingClientRect();

    const requiredWidth = reservedWidth + parametersWidth + bufferSpace;

    return { containerWidth, requiredWidth };
  }, [reservedWidth, bufferSpace]);

  useEffect(() => {
    if (isEditingParameter) {
      return;
    }

    const updateCollisionState = () => {
      const widths = getWidths();
      if (!widths) {
        return;
      }

      setShouldCollapseList((currentShouldCollapseList) =>
        getNextShouldCollapseList({
          shouldCollapseList: currentShouldCollapseList,
          ...widths,
        }),
      );
    };

    updateCollisionState();

    const element = containerRef.current;
    if (!element) {
      return;
    }

    resizeObserver.subscribe(element, updateCollisionState);
    return () => {
      resizeObserver.unsubscribe(element, updateCollisionState);
    };
  }, [getWidths, isEditing, isEditingParameter]);

  return {
    shouldCollapseList,
    containerRef,
    parameterListRef,
  };
}
